import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { Upload, FileText, X, CheckCircle, AlertCircle } from "lucide-react";

export default function DocumentUpload() {
  const [schemas, setSchemas] = useState([]);
  const [selectedSchema, setSelectedSchema] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => { api.get("/schemas").then(r => setSchemas(r.data)).catch(() => {}); }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (f) setFile(f);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError("Please select a file"); return; }
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (selectedSchema) fd.append("schema_id", selectedSchema);
      const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (result) return (
    <div className="max-w-lg mx-auto">
      <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
        <div className="w-12 h-12 bg-[#E5F0F2] rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="text-[#006080]" size={24} />
        </div>
        <h3 className="text-lg font-bold text-slate-900 mb-1">Document Uploaded</h3>
        <p className="text-sm text-slate-500 mb-1">Processing started. OCR + extraction running in background.</p>
        <p className="text-xs text-slate-400 mb-6">File: {result.original_filename}</p>
        <div className="flex gap-3 justify-center">
          <button
            data-testid="view-document-button"
            onClick={() => navigate(`/documents/${result.id}`)}
            className="h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors"
          >
            View Document
          </button>
          <button
            data-testid="upload-another-button"
            onClick={() => { setFile(null); setResult(null); }}
            className="h-9 px-4 bg-white border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50 transition-colors"
          >
            Upload Another
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900">Upload Document</h2>
        <p className="text-sm text-slate-500 mt-0.5">Upload any document for OCR processing and field extraction.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Schema selection */}
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-2">
            Document Schema (Optional)
          </label>
          <select
            data-testid="schema-selector"
            value={selectedSchema}
            onChange={(e) => setSelectedSchema(e.target.value)}
            className="w-full h-10 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white"
          >
            <option value="">No schema — OCR only</option>
            {schemas.map(s => <option key={s.id} value={s.id}>{s.name} {s.status === "approved" ? "✓" : "(draft)"}</option>)}
          </select>
          <p className="text-xs text-slate-400 mt-1.5">Select a schema to automatically extract structured fields from the document.</p>
        </div>

        {/* File drop zone */}
        <div
          data-testid="file-drop-zone"
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`bg-white border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer ${
            dragging ? "border-[#006080] bg-[#E5F0F2]" : "border-slate-200 hover:border-slate-300"
          }`}
          onClick={() => document.getElementById("file-input").click()}
        >
          <input
            id="file-input"
            data-testid="file-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.tiff"
            onChange={onDrop}
            className="hidden"
          />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText size={20} className="text-[#006080]" />
              <span className="text-sm font-medium text-slate-900">{file.name}</span>
              <button
                type="button"
                data-testid="remove-file-button"
                onClick={(e) => { e.stopPropagation(); setFile(null); }}
                className="text-slate-400 hover:text-red-500"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div>
              <Upload size={28} className="text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700">Drop file here or click to browse</p>
              <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG, WEBP, TIFF · Max 10MB</p>
            </div>
          )}
        </div>

        {error && (
          <div data-testid="upload-error" className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
            <AlertCircle size={14} className="flex-shrink-0" />
            {error}
          </div>
        )}

        <button
          data-testid="submit-upload-button"
          type="submit"
          disabled={!file || uploading}
          className="w-full h-10 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Uploading...
            </span>
          ) : "Upload & Process"}
        </button>
      </form>
    </div>
  );
}
